<template>
	<div id="top">
		<nav class="main-nav fix-on-scroll" :class="{ 'show-mobile-nav': showMobileNav }">
			<div class="row">
				<div class="columns column12">
					<div class="nav-bar">
						<button class="logo" aria-label="To homepage" @click="handleLogoClick">
							<img
								src="~/assets/images/logo-barbacoa-barbecue-restaurant-2026.png"
								loading="lazy"
								alt="Barbacoa - Barbecue Restaurant"
							/>
						</button>
						<div class="nav-content">
							<ul>
								<li class="menu-close" @click="hideMobileNav">
									<font-awesome-icon class="icon" icon="fal fa-xmark" size="1x" />
								</li>
								<li v-for="item in topmenu[locale]" :key="item.filename" @click="hideMobileNav">
									<nuxt-link :to="item.filename">
										{{ item.header }}
									</nuxt-link>
								</li>
							</ul>
						</div>
						<div class="buttons">
							<div class="menu-button mobile-only" @click="toggleMobileNav">
								<font-awesome-icon class="icon" icon="fal fa-bars" size="1x" />
							</div>
							<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
								<span> <font-awesome-icon class="nav-icon" icon="fal fa-phone" size="1x" /></span>
							</a>

							<a :href="`mailto:${defaults[locale].website.email}`">
								<span><font-awesome-icon class="nav-icon" icon="fal fa-envelope" size="1x" /></span>
							</a>

							<book-button class="button book-now cta reserve-now">
								<font-awesome-icon class="icon" icon="fal fa-calendar-alt" size="1x" />
								<span>{{ $t('bookNow') }}</span>
							</book-button>
						</div>
					</div>
				</div>
			</div>
		</nav>
	</div>
</template>

<script setup>
const { fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const route = useRoute();
const router = useRouter();

await fetchPage();
await fetchDefaults();

const showMobileNav = ref(false);

const { data: topmenu } = await useWebsiteFetch('topmenu');

const toggleMobileNav = () => {
	showMobileNav.value = !showMobileNav.value;
};

const hideMobileNav = () => {
	showMobileNav.value = false;
};

const handleLogoClick = () => {
	if (route.name?.startsWith('index')) {
		window.scrollTo(0, 0);
	} else {
		router.push('/');
	}
};
</script>

<style lang="scss" scoped>
.mobile-only {
	display: none !important;
}

.main-nav {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	box-shadow: 0 0 20px rgb(0 0 0 / 10%);

	ul li {
		display: inline-block;
	}
}

.nav-bar {
	display: flex;
	align-items: center;
	padding: 0;
	z-index: 9;
	position: relative;

	.buttons {
		display: flex;
		align-items: center;
		gap: 20px;
		font-size: 24px;

		svg:hover {
			color: var(--secondary-background-color);
		}

		.button {
			padding: 10px 25px;
			margin: 0;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.icon {
			display: none;
			margin-right: 5px;
		}

		.nav-icon {
			margin-right: 15px;
		}
	}
}

.logo {
	&:hover {
		cursor: pointer;
	}

	all: unset;
	padding: 20px;
	text-decoration: none;
	margin: 0;
	background: none;

	h3 {
		margin: 0;
		font-size: 24px;
	}

	img {
		max-width: 150px;
	}
}

.nav-content {
	flex-grow: 1;
	text-align: center;

	a {
		position: relative;
		text-decoration: none;
		margin: 0 15px;
		color: #fff;
		letter-spacing: -1.5%;
		font-size: 15px;
		text-transform: uppercase;
		font-weight: 500;
		overflow: hidden;

		&::before {
			content: '';
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%) scaleX(0);
			width: 100%;
			height: 100%;
			background: #c28420;
			padding: 3px 10px;
			z-index: -1;
			transition: transform 0.3s ease;
		}

		&.router-link-active::before,
		&:hover::before {
			transform: translate(-50%, -50%) scaleX(1);
		}
	}

	svg {
		&:hover {
			color: var(--secondary-background-color);
		}
	}

	.menu-close {
		color: #fff;
		display: none;
		font-size: 30px;
		cursor: pointer;
		position: absolute;
		top: 15px;
		width: auto;
		right: 20px;
		padding: 0;
		margin: 0;
	}
}

.lang-nav {
	margin: 0 0 25px;

	.language {
		margin: 20px 0;

		.flag::before {
			content: '';
			width: 16px;
			position: relative;
			top: 3px;
			height: 16px;
			margin-right: 6px;
			display: inline-block;
		}

		.flag.en::before {
			background: url('~/assets/images/en.png') no-repeat;
		}

		.flag.nl::before {
			background: url('~/assets/images/nl.png') no-repeat;
		}

		.flag.de::before {
			background: url('~/assets/images/de.png') no-repeat;
		}

		.flag.fr::before {
			background: url('~/assets/images/fr.png') no-repeat;
		}

		.flag.es::before {
			background: url('~/assets/images/es.png') no-repeat;
		}
	}

	img {
		width: 16px;
		float: left;
		margin: 0 5px 0 0;
	}

	a {
		width: 100%;
		float: left;
		color: var(--body-color);
		text-decoration: none;
		margin: 4px 0;
		text-transform: uppercase;
	}
}

.language-navigation:hover .lang-nav-sel,
.language-navigation:hover .fa-globe {
	color: var(--cta-color);
}

.lang-nav-sel {
	box-shadow: none;
	cursor: pointer;
	width: 30px;
	padding: 0 4px 0 0;
	color: #fff;
	border: none;
	text-transform: uppercase;
	font-size: 12px;
	font-family: sans-serif;
	appearance: none;
	background: url('~/assets/images/dropdown.png') right 1px center no-repeat;
	background-size: 7px;
	transition: color 0.3s ease-in-out;

	option {
		color: var(--body-color);
	}

	&::-ms-expand {
		display: none;
	}
}

.lang-select {
	padding: 8px 20px;
	margin: 0 10px;
	border-left: 1px solid #e5e5e5;
	position: relative;
	cursor: pointer;
	text-transform: uppercase;
	font-size: 13px;
	display: flex;
	flex-flow: row nowrap;
	align-items: center;

	span {
		margin: 0 4px;
	}

	a {
		text-decoration: none;
	}

	svg {
		font-size: 12px;
	}
}

.lang-wrapper {
	display: none;
	position: absolute;
	top: 35px;
	left: 3px;
	right: 3px;
	padding: 10px 9px;
	text-align: center;
	background: var(--primary-background-color);
	box-shadow: 0 18px 18px rgb(0 0 0 / 10%);
	z-index: 90;

	&.active,
	a {
		display: flex;
		flex-flow: column nowrap;
		place-content: space-between center;
		color: #000 !important;
		font-size: 13px;
		gap: 5px;
	}

	a {
		width: 100%;
		flex-direction: row;
	}

	img {
		width: 16px;
		height: 16px;
		margin: 2px 0 0;
	}
}

.language-navigation .fa-globe {
	margin: 0 2px 0 0;
	color: var(--body-color);
	transition: color 0.3s ease-in-out;
}

@media (max-width: 1200px) {
	.menu-button {
		text-transform: uppercase;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: space-between;
		margin: 0 10px 0 0;
		cursor: pointer;
		color: #fff;

		svg {
			font-size: 22px;
		}

		span {
			margin: 0 0 0 5px;
		}
	}

	.nav-bar .buttons {
		gap: 0;

		.button {
			padding: 16px;
		}

		.menu-button {
			margin: 0 20px;

			.icon {
				font-size: 22px;
			}
		}

		.nav-icon {
			display: none;
		}

		.icon {
			margin-right: 0;
			font-size: 16px;
			display: unset;
		}

		span {
			display: none;
		}
	}

	.mobile-only {
		display: flex !important;

		.menu-button {
			margin-right: 20px;
		}

		&.lang-nav {
			display: inline-block !important;
			margin: 30px 0 0;
			padding: 0 10px;
		}
	}

	.language-navigation {
		display: none;
	}

	.main-nav {
		position: fixed !important;
		top: 0;
		box-shadow: 0 0 10px rgb(0 0 0 / 10%);

		&.show-mobile-nav {
			.nav-content ul {
				display: flex;
				flex-direction: column;
				justify-content: center;
				opacity: 1;
				visibility: visible;
				transition: opacity 0.3s ease-in-out;

				li:not(.menu-close) {
					width: 100%;
				}

				li:not(.lang-nav, .menu-close) {
					margin: 7px 0;

					a {
						font-size: 26px;
						font-family: var(--heading-font-family);
						font-weight: 700;
						padding: 5px 15px;

						&:hover {
							background: var(--menu-bg-color-hover);
						}
					}
				}
			}
		}

		ul {
			position: fixed;
			inset: 0;
			height: 100vh;
			background: var(--menu-background-color);
			backdrop-filter: blur(10px);
			text-align: center;
			padding: 20px;
			opacity: 0;
			visibility: hidden;
			overflow-y: scroll;
		}
	}

	.nav-content .menu-close {
		display: block;
	}

	.logo {
		h3 {
			font-size: 22px;
		}
	}
}

@media (max-width: 880px) {
	.logo {
		padding: 12px;
	}

	.nav-content {
		a {
			&::before {
				padding: 0 1px;
			}
		}
	}
}

@media (max-width: 580px) {
	.nav-bar .buttons span {
		display: none;
	}

	.nav-bar .logo {
		font-size: 22px;
	}
}

.fix-on-scroll {
	transition:
		background-color 0.5s ease,
		backdrop-filter 0.5s ease;

	&.fixed {
		background-color: var(--menu-background-color);
		backdrop-filter: blur(10px);

		.buttons {
			svg:hover {
				color: var(--tertiary-background-color);
			}
		}
	}
}
</style>
